import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import qs from 'qs';
import { useRouter } from 'next/router';
import './../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import * as Sentry from '@sentry/nextjs';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  Layout,
  Body,
  Card,
  Heading,
  FormDescription,
  Text,
  PageHead,
} from '@/components/Index';
import useUrlQuery from '@/components/hooks/useUrlQuery';

const ShowError = dynamic(() => import('@/components/ShowError'));
const ShowAlert = dynamic(() => import('@/components/ShowAlert'));
const LoginSuccess = dynamic(() => import('@/components/LoginSuccess'));
import Loading from '@/components/Loading';
const OktaSignInWidget = dynamic(
  () => import('@/components/OktaSignInWidget'),
  { ssr: false },
);

import { login } from '@/services/index';
import { filterOriginUrl } from '@/libs/index';
import { API_ENDPOINT } from '@/configs/index';

import {
  convertErrorCode,
  getAuthClient,
  getIdpLoginHref,
  startEmployeeIdpLogin,
} from '@/libs/okta';
import {
  buildMcpCallbackURL,
  getMcpRelayFromQuery,
  isRelayFresh,
} from '@/libs/mcpRelay';
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
} from '@/libs/storage';
import { removeCookie } from '@/libs/cookie';
const ORIGINAL_URL_KEY = '__vungle_original_url';
const PREFILLED_EMAIL_KEY = '__vungle_prefilled_email';
const IMPERSONATED_EMAIL_KEY = '__vungle_impersonated_email';
const ORIGINAL_URL_AFTER_LOGOUT_KEY = '__vungle_original_after_logout_url';
const ORIGINAL_AUTH_METHOD = 'auth_method';
// Marker persisted across the Okta round-trip when the local admin-mcp opens
// this page with ?mcp=1&port=<port>&nonce=<nonce>. See PAB-8053 (Admin MCP).
const MCP_RELAY_KEY = '__vungle_mcp_relay';
// Set only by an explicit Employee-Login click while the mcp marker is present,
// so the app JWT is forwarded to the local MCP loopback ONLY on a real user
// gesture — never on a silent prompt:'none' auto-login. See PAB-8053 (C1).
const MCP_USER_INITIATED_KEY = '__vungle_mcp_user_initiated';
// PAB-8053 Option C (OIDC): when the auth service's OAuth interaction bridge
// sends the browser here to complete Employee-Login, it passes ?oidc_return=<abs
// URL back to /oidc/interaction/:uid/login>. After a successful login we hand the
// app JWT back to that bridge via the URL *fragment* (never sent to the server /
// logs). Persisted so it survives the Okta round-trip. NOTE: unlike the legacy
// MCP relay this does NOT need a user-initiated guard — the anti-silent-consent
// property is enforced server-side by the mandatory consent step (spec §0.4):
// establishing the login session alone cannot yield an authorization code.
const OIDC_RETURN_PARAM = 'oidc_return';
const OIDC_RETURN_KEY = '__vungle_oidc_return';

function getOidcReturnFromQuery() {
  if (typeof window === 'undefined') return null;
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  return parsed[OIDC_RETURN_PARAM] || null;
}

// Guardrail against token exfiltration via an open redirect: forward the app JWT
// ONLY to the auth service's own OIDC interaction bridge. Bound to the EXACT
// configured issuer origin (the auth API endpoint) — scheme + host must match,
// and the path must be under the interaction bridge prefix. No wildcard host
// allowlist, no http-loopback allowance.
function isAllowedOidcReturn(url) {
  try {
    if (!API_ENDPOINT) return false;
    const u = new URL(url);
    const expected = new URL(API_ENDPOINT);
    return (
      u.origin === expected.origin &&
      u.pathname.startsWith('/oidc/interaction/')
    );
  } catch (_) {
    return false;
  }
}

// Explicit phases for the login flow. Driving the UI off a single phase makes
// the process predictable: every async path ends in exactly one terminal phase
// (SUCCESS / ERROR / SHOW_FORM) and there is no way to get stuck on the loader.
const PHASE = {
  BOOTSTRAPPING: 'bootstrapping', // parsing redirect / deciding SSO
  EXCHANGING: 'exchanging', // calling our /login with the okta token
  SUCCESS: 'success',
  ERROR: 'error',
  SHOW_FORM: 'show_form',
};

const idpLoginHref = getIdpLoginHref();

function getEmailAlert(email) {
  return (
    <p>
      The email <span className="font-bold">{email}</span> already has an
      account. Please use existing credentials to Log In.
    </p>
  );
}

// why are we storing a key:value pair? because local-storage library json stringifies the email and
// that breaks emails like qamar2ham+100@gmail.com that have special characters.
function getImpersonatedEmail() {
  let impersonatedEmail;
  if (typeof window !== 'undefined') {
    impersonatedEmail = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }).asuserEmail;
  }
  return { impersonatedEmail };
}

function getOriginal() {
  if (typeof window !== 'undefined') {
    return qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }).original;
  }
}

const ctrlDomains = [
  /(pub|adv)-ctrl-.+\.vungle\.io/,
  /(pubadmin|ctrl)\.vungle\.com/,
];
function verifyCallback(isInternal, originalUrl) {
  if (!isInternal) {
    for (const domain of ctrlDomains) {
      if (domain.test(originalUrl)) {
        return null;
      }
    }
  }

  return originalUrl;
}

function checkIsSSOLoginError(err) {
  return (
    Array.isArray(err?.messages) &&
    err?.messages.length > 0 &&
    err?.messages[0].indexOf('login with SSO') > -1
  );
}

export default function Login() {
  const router = useRouter();
  const original = router.query.original;
  const emailFromUrl = useUrlQuery('email');
  const { impersonatedEmail } = getImpersonatedEmail();
  const [prefilledEmail, setPrefilledEmail] = useState('');
  const [responseError, setResponseError] = useState(false);
  const [isSSOLoginError, setIsSSOLoginError] = useState(false);
  const [loginSuccessResults, setLoginSuccessResults] = useState(null);
  const originalUrl = filterOriginUrl(getOriginal());
  const isMountedRef = useRef(true);
  const redirectHandledRef = useRef(false);
  const [phase, setPhase] = useState(PHASE.BOOTSTRAPPING);

  const loginFormSchema = yup.object().shape({
    email: yup.string().email().required('Email is required.'),
    password: yup.string().required('Password is required.'),
  });

  const { setValue, getValues } = useForm({
    resolver: yupResolver(loginFormSchema),
  });

  const handleSuccess = ({ data }, originalUrl) => {
    if (!isMountedRef.current) return;
    removeFromStorage(ORIGINAL_URL_KEY);
    removeFromStorage(PREFILLED_EMAIL_KEY);
    removeFromStorage(IMPERSONATED_EMAIL_KEY);
    sessionStorage.removeItem(ORIGINAL_AUTH_METHOD);
    const callback = verifyCallback(data.isInternal, originalUrl);
    const dataWithOriginalUrl = callback
      ? { ...data, originalUrl: callback }
      : data;
    setLoginSuccessResults(dataWithOriginalUrl);
    setResponseError(false);
    setPhase(PHASE.SUCCESS);
  };

  const handleFailure = async (err) => {
    if (!isMountedRef.current) return;
    const isLoginSSOError = checkIsSSOLoginError(err);
    // login error means all errors returned from login endpoint except the unknown errors, and the errors are caught by our auth service,
    const isLoginError = Array.isArray(err.messages) && err.messages.length > 0;

    setTimeout(async () => {
      // we need to sign out the token when the errors caught by login endpoint of auth service, cuz it is not a valid token even it is auth by okta already
      if (isLoginSSOError || isLoginError) {
        removeCookie('username');
        removeCookie('token');

        const originalUrl = original || '/';
        setToStorage(ORIGINAL_URL_AFTER_LOGOUT_KEY, originalUrl);

        await getAuthClient()?.signOut({
          postLogoutRedirectUri:
            process.env.NEXT_PUBLIC_OKTA_LOGOUT_REDIRECT_URL,
          revokeAccessToken: false,
        });
      }
    }, 3000);

    setIsSSOLoginError(isLoginSSOError);
    setResponseError(err);
    setPhase(PHASE.ERROR);
  };

  // this runs on page refresh or after Okta redirect
  useEffect(() => {
    const authClient = getAuthClient();
    if (!authClient) {
      return;
    }

    const afterOktaLogin = async (email) => {
      const originalUrl = getFromStorage(ORIGINAL_URL_KEY);
      try {
        const tokens = await authClient.tokenManager.getTokens();

        if (!tokens.accessToken.accessToken) {
          Sentry.captureException({
            message: `null access token for email ${email}`,
            tokens,
            idToken: tokens.idToken.idToken,
          });
        }
        const impersonatedEmail = getFromStorage(IMPERSONATED_EMAIL_KEY);
        const res = await login({
          email: impersonatedEmail ? `${email}::${impersonatedEmail}` : email,
          token: tokens.accessToken.accessToken,
          method: sessionStorage.getItem(ORIGINAL_AUTH_METHOD),
        });
        // admin-mcp relay: if this login was started by the local MCP, hand the
        // app JWT to its loopback listener instead of redirecting into the app.
        const relayRaw = getFromStorage(MCP_RELAY_KEY);
        const userInitiated = getFromStorage(MCP_USER_INITIATED_KEY);
        if (relayRaw) {
          removeFromStorage(MCP_RELAY_KEY); // single-use
          removeFromStorage(MCP_USER_INITIATED_KEY);
          let relay = null;
          try {
            relay = JSON.parse(relayRaw);
          } catch (_) {
            relay = null;
          }
          // Forward the JWT to the loopback ONLY when the user explicitly
          // started this MCP login via the Employee-Login button (userInitiated)
          // AND the marker is fresh — never on a silent prompt:'none' return, so
          // there is no zero-click exfiltration path. A stale marker from an
          // abandoned flow is also ignored.
          if (userInitiated && isRelayFresh(relay, Date.now())) {
            const callbackURL = buildMcpCallbackURL(relay, res?.data?.token);
            if (callbackURL) {
              window.location.href = callbackURL;
              return;
            }
          }
        }
        // PAB-8053 Option C (OIDC): hand the app JWT back to the auth service's
        // OAuth interaction bridge via the URL fragment, then let the server-side
        // consent step gate code issuance.
        const oidcReturn = getFromStorage(OIDC_RETURN_KEY);
        if (oidcReturn) {
          removeFromStorage(OIDC_RETURN_KEY); // single-use
          const jwt = res?.data?.token;
          if (jwt && isAllowedOidcReturn(oidcReturn)) {
            window.location.href = `${oidcReturn}#token=${encodeURIComponent(jwt)}`;
            return;
          }
        }
        handleSuccess(res, originalUrl);
      } catch (e) {
        handleFailure(e);
      }
    };

    async function load() {
      // PAB-8053 Option C (OIDC): persist the interaction return target so it
      // survives the Okta round-trip. Only stored on the initial visit (when the
      // query param is present), consumed in afterOktaLogin.
      const oidcReturn = getOidcReturnFromQuery();
      if (oidcReturn && isAllowedOidcReturn(oidcReturn)) {
        setToStorage(OIDC_RETURN_KEY, oidcReturn);
      }

      // admin-mcp relay: if opened by the local MCP (?mcp=1&port&nonce) and this
      // is not already an Okta redirect return, persist a timestamped marker so
      // it survives the Okta round-trip, then show the normal login form and
      // STOP. Crucially we do NOT run the silent prompt:'none' SSO probe on this
      // path: the JWT is only forwarded to the loopback after an explicit
      // Employee-Login click (which sets MCP_USER_INITIATED_KEY). Otherwise a
      // crafted ?mcp=1 link could silently drive an already-authenticated
      // employee through login and exfiltrate a token with zero interaction.
      const mcpRelay = getMcpRelayFromQuery();
      if (
        mcpRelay &&
        mcpRelay.port &&
        mcpRelay.nonce &&
        !authClient.isLoginRedirect()
      ) {
        setToStorage(
          MCP_RELAY_KEY,
          JSON.stringify({ ...mcpRelay, ts: Date.now() }),
        );
        setPhase(PHASE.SHOW_FORM);
        return;
      }

      // we always try to login with okta if okta first, because if okta session cookie exists, we can just pull the user
      // because the user is already SSO'ed
      // if this is a redirect after signInWithRedirect
      if (authClient.isLoginRedirect()) {
        // parseFromUrl()/transaction state is single-use, so guard against a
        // second run (re-render or React 18 StrictMode double-mount).
        if (redirectHandledRef.current) return;
        redirectHandledRef.current = true;
        setPhase(PHASE.EXCHANGING);
        // this only happens after okta's own redirect (SSO one). it means only after signInWithRedirect
        try {
          // this means we have accessToken in url fragment
          const res = await authClient.token.parseFromUrl();
          authClient.tokenManager.setTokens(res.tokens);
          // this means if it accessToken exists in OUR domain, not okta's
          // if not authenticated, the user is presented with the login page
          const isAuthenticated = await authClient.isAuthenticated();
          if (isAuthenticated) {
            const user = await authClient.token.getUserInfo();
            await afterOktaLogin(user.email);
          }
        } catch (e) {
          const prefilledEmail = getFromStorage(PREFILLED_EMAIL_KEY);
          if (prefilledEmail) {
            setPrefilledEmail(prefilledEmail);
          }
          const error = convertErrorCode(e.errorCode);
          if (error.code !== 401) {
            handleFailure(error);
          } else {
            // 401 here is the benign "no active Okta session" outcome (e.g. the
            // silent prompt=none probe returning login_required). Drop to the
            // manual login form rather than hanging on the loader.
            setPhase(PHASE.SHOW_FORM);
          }
        }
      } else {
        setPhase(PHASE.SHOW_FORM);
        // try to login with okta SSO (okta's session cookie).
        // prompt=none can reject with AuthApiError on transient Okta failures
        // or when the browser blocks third-party cookies; catch so the
        // rejection does not become an unhandled window.onunhandledrejection
        // (Sentry issue SSO-NEXT-AB / PAB-7907).
        try {
          await authClient.signInWithRedirect({
            prompt: 'none',
            originalUri: window.location.href,
            nonce: process.env.NEXT_PUBLIC_OKTA_NONCE,
          });
        } catch (e) {
          Sentry.captureException(e, {
            tags: { flow: 'okta-sso-prompt-none' },
          });
        }
      }
    }

    if (emailFromUrl) {
      setPrefilledEmail(emailFromUrl);
      setToStorage(PREFILLED_EMAIL_KEY, emailFromUrl);
    }
    if (impersonatedEmail) {
      setToStorage(IMPERSONATED_EMAIL_KEY, impersonatedEmail);
    }

    load();
    // handleFailure is a stable closure; including it would re-run the SSO bootstrap on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromUrl, impersonatedEmail]);

  useEffect(() => {
    // after okta redirect and token extraction, the `original` param is removed from URL, so we need to store it in local storage
    if (originalUrl) {
      setToStorage(ORIGINAL_URL_KEY, originalUrl);
    }
  }, [originalUrl]);

  useEffect(() => {
    const emailValue = getValues('email');
    if (!emailValue) {
      setValue('email', prefilledEmail);
    }
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false; // guard async setState, but DON'T null results
    };
  }, []);

  // Safety net: never let the EXCHANGING loader spin forever.
  useEffect(() => {
    if (phase !== PHASE.EXCHANGING) return;
    const t = setTimeout(() => {
      if (isMountedRef.current && phase === PHASE.EXCHANGING) {
        handleFailure({
          messages: ['Login timed out. Please try again.'],
          code: 408,
        });
      }
    }, 20000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <>
      <PageHead title="Login" description="Liftoff login page." />
      <Layout>
        <Body>
          {phase === PHASE.SHOW_FORM && (
            <Card dataTestId="user-login">
              <Heading text="Oh hello there!" />
              <FormDescription content="Log in to Liftoff Direct/Liftoff Monetize." />
              <OktaSignInWidget />
              {responseError && (
                <div className="mt-16">
                  <ShowError err={responseError} />
                </div>
              )}
              {prefilledEmail && (
                <div className="mt-8">
                  <ShowAlert>{getEmailAlert(prefilledEmail)}</ShowAlert>
                </div>
              )}
              <div className="mt-32">
                <Text>
                  Don&apos;t have an account yet?
                  <Link href="/signup" data-testid="signup-link">
                    {' '}
                    Sign up
                  </Link>
                </Text>
                <div className="mt-64">
                  <a
                    href={idpLoginHref}
                    onClick={(e) => {
                      // Record the explicit user gesture so afterOktaLogin only
                      // forwards the app JWT to the local MCP when the employee
                      // actively chose Employee Login (see PAB-8053 C1). No-op
                      // for a normal (non-mcp) login.
                      if (getMcpRelayFromQuery()) {
                        setToStorage(MCP_USER_INITIATED_KEY, '1');
                        // MCP path only: go through okta-auth-js instead of the raw
                        // href so the OAuth transaction meta is stored. The mcp flow
                        // skips the prompt:none probe that normally primes it, so the
                        // raw href would leave parseFromUrl() with nothing to read
                        // ("Unable to retrieve OAuth redirect params from storage").
                        // Normal (non-mcp) login keeps the unchanged href below.
                        e.preventDefault();
                        startEmployeeIdpLogin(getAuthClient()).catch((err) => {
                          Sentry.captureException(err, {
                            tags: { flow: 'mcp-relay-idp-login' },
                          });
                        });
                      }
                    }}
                  >
                    Employee Login
                  </a>
                </div>
              </div>
            </Card>
          )}
          {isSSOLoginError && (
            <div className="absolute inset-0 w-full h-full "></div>
          )}
          {(phase === PHASE.BOOTSTRAPPING || phase === PHASE.EXCHANGING) && (
            <Loading />
          )}
          {phase === PHASE.ERROR && (
            <div className="mt-16">
              <ShowError err={responseError} />
            </div>
          )}
          {phase === PHASE.SUCCESS && loginSuccessResults && (
            <LoginSuccess results={loginSuccessResults} />
          )}
        </Body>
      </Layout>
    </>
  );
}
